import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Toaster from 'components/Toaster';
import DetailsTab from './components/DetailsTab';
import { useCreateAccount } from './gql/useCreateAccount';

// Page wrapper - ensures full viewport coverage with theme background
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme?.colors?.background?.primary ||
    (theme?.name === 'dark' ? '#1a1a2e' : '#f8fafc')};
  font-family: ${({ theme }) =>
    theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'};
`;

// Page container - from StyleGuide Layout patterns
const Container = styled.div`
  padding: 30px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

// Page header - from StyleGuide Overview pattern
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div``;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

// Button wrapper to control width for header buttons
const HeaderButton = styled.div`
  width: auto;

  button {
    width: auto;
    padding: 10px 24px;
  }
`;

// Inner component that uses theme
const CreateAccountContent = () => {
  const history = useHistory();
  const location = useLocation();

  // Determine route prefix for context-aware routing
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const [formValues, setFormValues] = useState({
    // Business details
    business_dba: '',
    legal_name: '',
    phone: '',
    country_code: '+1',
    calling_code: '1',
    primary_contact: '',
    title: '',
    // Promoter information
    promoter_name: '',
    promoter_email: '',
    promoter_phone: '',
    website: '',
    org_name: '',
    promoter_type: '',
    // Reserved Seat Settings
    best_available: true,
    orphaned_seats: null,
    // Addresses
    track: {
      name: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zipcode: '',
      type: 'track',
    },
    mailing: {
      name: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zipcode: '',
      type: 'mailing',
    },
    shipping: {
      name: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zipcode: '',
      type: 'shipping',
    },
  });
  const [toaster, setToaster] = useState({ isOpen: false });

  const { createAccount, loading: saving } = useCreateAccount();

  // Helper to update form values (similar to Formik setFieldValue)
  const setFieldValue = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancel = () => {
    history.goBack();
  };

  // Helper to check if an address has any meaningful data
  const hasAddressData = (address) => {
    if (!address) return false;
    return (
      address.name ||
      address.address_line_1 ||
      address.city ||
      address.state ||
      address.zipcode
    );
  };

  // Helper to format address for API (only include if has data)
  const formatAddress = (address) => {
    if (!hasAddressData(address)) return null;
    return {
      name: address.name || '',
      address_line_1: address.address_line_1 || '',
      address_line_2: address.address_line_2 || '',
      city: address.city || '',
      state: address.state || '',
      zipcode: address.zipcode || '',
    };
  };

  const handleCreate = async () => {
    // Build the input object with all fields
    const input = {
      // Business Details
      business_dba: formValues.business_dba,
      legal_name: formValues.legal_name,
      primary_contact: formValues.primary_contact,
      title: formValues.title,
      phone: formValues.phone,
      country_code: formValues.country_code?.replace('+', ''),
      calling_code: formValues.calling_code,
      // Promoter Information
      promoter_name: formValues.promoter_name,
      promoter_email: formValues.promoter_email,
      promoter_phone: formValues.promoter_phone,
      website: formValues.website,
      org_name: formValues.org_name,
      promoter_type: formValues.promoter_type,
      // Reserved Seat Settings
      best_available: formValues.best_available,
      orphaned_seats: formValues.orphaned_seats || null,
      // Addresses - only include if they have data
      track: formatAddress(formValues.track),
      mailing: formatAddress(formValues.mailing),
      shipping: formatAddress(formValues.shipping),
    };

    const result = await createAccount(input);

    if (result.success) {
      // Redirect to the edit page for the new account
      const newAccountId = result.data?.id;
      if (newAccountId) {
        history.push({
          pathname: `${routePrefix}/accounts/editAccount/${newAccountId}`,
          state: {
            toaster: {
              type: 'success',
              title: 'Success!',
              message:
                'Account created successfully. You can now configure additional settings.',
            },
          },
        });
      } else {
        // Fallback to accounts list if no ID returned
        history.push(`${routePrefix}/accounts`);
      }
    } else {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: result.error || 'Failed to create account. Please try again.',
      });
    }
  };

  return (
    <PageWrapper>
      <Container>
        <PageHeader>
          <HeaderLeft>
            <Title>Add Account</Title>
            <Subtitle>
              Enter the basic account details to get started. You can configure
              additional settings after creating the account.
            </Subtitle>
          </HeaderLeft>
          <HeaderRight>
            <HeaderButton>
              <ButtonV2 type="button" onClick={handleCancel}>
                Cancel
              </ButtonV2>
            </HeaderButton>
            <HeaderButton>
              <ButtonV2
                elevated
                type="button"
                onClick={handleCreate}
                disabled={saving}
              >
                {saving ? 'Creating...' : 'Create Account'}
              </ButtonV2>
            </HeaderButton>
          </HeaderRight>
        </PageHeader>

        <DetailsTab values={formValues} setFieldValue={setFieldValue} />
      </Container>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </PageWrapper>
  );
};

// Main component wrapped with theme provider
const CreateAccountV2 = () => {
  return (
    <EnhancedThemeProvider>
      <CreateAccountContent />
    </EnhancedThemeProvider>
  );
};

export default CreateAccountV2;
